import type { TablePaginationConfig } from 'antd/es/table';
import type { ColumnsType } from 'antd/es/table/interface';
import dayjs from 'mixins/dayjsConfig.js';
import { localize } from 'mixins/helpers';
import React from 'react';
import { ApiCallStatistic } from '/react4xp/parts/admin-api-call-statistics/types';
import {
    DEFAULT_PAGE_SIZE,
    DEFAULT_SORT_ORDER,
    DISPLAY_DATE_FORMAT,
} from '../utils/constants';
import { CallStatisticsSortState } from '../utils/types';

interface GetCallStatisticsColumnsParams {
    apiNames: Record<string, string>;
    pagination: TablePaginationConfig;
    sort: CallStatisticsSortState;
    translations: Record<string, string>;
}

export const getCallStatisticsColumns = ({
    apiNames,
    pagination,
    sort,
    translations,
}: GetCallStatisticsColumnsParams): ColumnsType<ApiCallStatistic> => [
    {
        title: localize('admin-api-stat-column-number', translations),
        key: 'number',
        width: 70,
        align: 'center',
        render: (_value, _record, index) =>
            ((pagination.current || 1) - 1) * (pagination.pageSize || DEFAULT_PAGE_SIZE) +
            index +
            1,
    },
    {
        title: localize('admin-api-stat-taxpayer-code', translations),
        dataIndex: 'taxpayerCode',
        key: 'taxpayerCode',
        align: 'center',
        sorter: true,
        sortOrder: sort.field === 'taxpayerCode' ? sort.order : null,
    },
    {
        title: localize('admin-api-stat-column-api', translations),
        key: 'apiName',
        render: (_value, record) => apiNames[record.apiCode] || '-',
    },
    {
        title: localize('admin-api-stat-column-api-code', translations),
        dataIndex: 'apiCode',
        key: 'apiCode',
        sorter: true,
        sortOrder: sort.field === 'apiCode' ? sort.order : null,
    },
    {
        title: localize('admin-api-stat-column-date', translations),
        dataIndex: 'statDate',
        key: 'statDate',
        align: 'center',
        sorter: true,
        defaultSortOrder: DEFAULT_SORT_ORDER,
        sortOrder: sort.field === 'statDate' ? sort.order : null,
        render: (value: string) => (value ? dayjs(value).format(DISPLAY_DATE_FORMAT) : '-'),
    },
    {
        title: localize('admin-api-stat-column-request-count', translations),
        dataIndex: 'requestCount',
        key: 'requestCount',
        align: 'center',
        sorter: true,
        sortOrder: sort.field === 'requestCount' ? sort.order : null,
    },
    {
        title: localize('admin-api-stat-column-success-count', translations),
        dataIndex: 'successCount',
        key: 'successCount',
        align: 'center',
    },
    {
        title: localize('admin-api-stat-column-error-count', translations),
        dataIndex: 'errorCount',
        key: 'errorCount',
        sorter: true,
        align: 'center',
        sortOrder: sort.field === 'errorCount' ? sort.order : null,
        render: (value: number) => (
            <span className={value > 0 ? 'admin-api-call-statistics__error-count' : ''}>
                {value}
            </span>
        ),
    },
];